import React from "react";
import { styled } from "@twilio-paste/core/styling-library";
import { Box } from "@twilio-paste/core/box";
import { Button } from "@twilio-paste/core/button";
import { Flex } from "@twilio-paste/core/flex";
import { Heading } from "@twilio-paste/core/heading";
import { Paragraph } from "@twilio-paste/core/paragraph";
import { Text } from "@twilio-paste/core/text";
import { SuccessIcon } from "@twilio-paste/icons/esm/SuccessIcon";
import { SupportIcon } from "@twilio-paste/icons/esm/SupportIcon";

import {
  IntelligentDiscoveryHomeBlock,
  NewChatFormValues,
  WiseOwlElementIds,
} from "@twiliointernal/e2-commons-shared-definitions";
import { constants, copy } from "../../definitions";
import { useWiseOwlContext } from "../../../../../hooks";

interface NoMessageErrorsPresentOnboardingProps {
  block: IntelligentDiscoveryHomeBlock;
}

const NoMessageErrorsPresentContainer = styled(Box)`
  [data-paste-element="MESSAGE_STATUS_CONTAINER"] {
    padding-top: 24px;
    padding-bottom: 24px;
    margin-bottom: 24px;
  }
  [data-paste-element="HEADING_CONTAINER"] {
    margin-bottom: 8px;
  }
  [data-paste-element="MESSAGE_STATUS_HEADER"] {
    margin-left: 8px;
    margin-bottom: 0;
  }
  [data-paste-element="MESSAGE_STATUS_COPY"] {
    font-weight: 600;
    font-size: 16px;
  }
  [data-paste-element="MESSAGE_ERRORS_BTN"],
  [data-paste-element="HELP_ME_BTN"],
  [data-paste-element="SCORE_BTN"] {
    margin-bottom: 8px;
  }
`;

const NoMessageErrorsPresentHome: React.FC<
  NoMessageErrorsPresentOnboardingProps
> = ({ block }) => {
  const {
    handlers: { handleStartNewChat },
  } = useWiseOwlContext();
  const {
    values: { lastNDays },
  } = block;

  const handleOnStartNewChat = (data: NewChatFormValues): void => {
    if (handleStartNewChat) {
      handleStartNewChat(data);
    }
  };

  return (
    <NoMessageErrorsPresentContainer
      element={WiseOwlElementIds.WISE_OWL_IDA_HOME_NO_ERROR}
      marginTop="space130"
    >
      <Box
        element={constants.MESSAGE_STATUS_CONTAINER}
        data-testid="intelligent-assistant-prechat-no-error-state-message-container"
        marginBottom="space70"
      >
        <Flex
          hAlignContent="left"
          vAlignContent="center"
          marginBottom="space30"
          element={constants.HEADING_CONTAINER}
        >
          <SuccessIcon
            decorative={true}
            size="sizeIcon30"
            color="colorTextIconAvailable"
          />
          <Heading
            as="h2"
            variant="heading20"
            element={constants.MESSAGE_STATUS_HEADER}
          >
            Great news!
          </Heading>
        </Flex>
        <Paragraph element={constants.MESSAGE_STATUS_COPY}>
          {copy.noMessageErrorsPresent.messageStatusCopy(lastNDays)}
        </Paragraph>
        <Text as="span" fontWeight="fontWeightNormal" fontSize="fontSize30">
          {" "}
          {copy.noMessageErrorsPresent.haveQuestions}
        </Text>
      </Box>
      <Box
        data-testid="intelligent-assistant-prechat-no-error-state-btn-container"
        marginTop="space50"
      >
        <Heading as="h6" variant="heading60">
          {copy.noMessageErrorsPresent.headers.sampleQuestions}
        </Heading>
        <Box marginTop="space40">
          <Button
            variant="secondary"
            size="rounded_small"
            element={constants.HELP_ME_BTN}
            data-testid="intelligent-assistant-prechat-no-error-state-help-me-btn"
            onClick={() =>
              handleOnStartNewChat({
                input: constants.prompts.helpMe,
              })
            }
          >
            <SupportIcon
              decorative={true}
              size="sizeIcon30"
              color="colorText"
            />
            {copy.noMessageErrorsPresent.callToAction.helpMe}
          </Button>
          <Button
            variant="secondary"
            size="rounded_small"
            element={constants.MESSAGE_ERRORS_BTN}
            data-testid="intelligent-assistant-prechat-no-error-state-message-errors-btn"
            onClick={() =>
              handleOnStartNewChat({
                input: constants.prompts.commonMessageErrors,
              })
            }
          >
            <SupportIcon
              decorative={true}
              size="sizeIcon30"
              color="colorText"
            />
            {copy.noMessageErrorsPresent.callToAction.commonMessageErrors}
          </Button>
          <Button
            variant="secondary"
            size="rounded_small"
            element={constants.SCORE_BTN}
            data-testid="intelligent-assistant-prechat-no-error-state-score-btn"
            onClick={() =>
              handleOnStartNewChat({
                input: constants.prompts.DeliverabilityScore,
              })
            }
          >
            <SupportIcon
              decorative={true}
              size="sizeIcon30"
              color="colorText"
            />
            {copy.noMessageErrorsPresent.callToAction.deliverabilityScore}
          </Button>
          <Button
            variant="secondary"
            size="rounded_small"
            data-testid="intelligent-assistant-prechat-no-error-state-undelivered-failed-status-btn"
            onClick={() =>
              handleOnStartNewChat({
                input: constants.prompts.failedVsUndelivered,
              })
            }
          >
            <SupportIcon
              decorative={true}
              size="sizeIcon30"
              color="colorText"
            />
            {copy.noMessageErrorsPresent.callToAction.deliveredVersusFailed}
          </Button>
        </Box>
      </Box>
    </NoMessageErrorsPresentContainer>
  );
};

export default NoMessageErrorsPresentHome;
